import type { TFunction } from 'i18next';

import {
  ANALYSIS_PHASES,
  FACT_KEYS,
  type AnalysisPhase,
  type AnalysisRun,
  type NormalizedAnalysisContent,
} from '../../../types/projectAnalysis';

/** 1-based step of a run's phase (0 while queued). */
export const phaseStep = (phase?: AnalysisPhase | null): number =>
  phase ? ANALYSIS_PHASES.indexOf(phase) + 1 : 0;

/** "Rédaction de l'analyse (4/4)" or "En attente…" while queued. */
export function phaseLabel(run: Pick<AnalysisRun, 'status' | 'phase'>, t: TFunction): string {
  if (run.status === 'queued' || !run.phase) return t('projects.runs.phase.queued');
  return t('projects.runs.phaseStep', {
    label: t(`projects.runs.phase.${run.phase}`),
    step: phaseStep(run.phase),
    total: ANALYSIS_PHASES.length,
  });
}

const line = (label: string, value?: string | null): string => (value?.trim() ? `- **${label}** : ${value.trim()}` : '');

/** The analysis as markdown (for « Copier en markdown »); concept ids stay between backticks. */
export function analysisToMarkdown(title: string, c: NormalizedAnalysisContent, t: TFunction): string {
  const out: string[] = [`# ${t('projects.analysis.markdownTitle', { title })}`];
  const section = (heading: string, body: string[]) => {
    const lines = body.filter(Boolean);
    if (lines.length) out.push('', `## ${heading}`, '', ...lines);
  };

  section(t('projects.analysis.sections.understood'), [
    c.understood,
    '',
    ...FACT_KEYS.map((k) => line(t(`projects.analysis.facts.${k}`), c.facts[k])),
  ]);
  section(
    t('projects.analysis.sections.related'),
    c.related.map((r) => `- ${r.title}${r.reason ? ` — ${r.reason}` : ''}`)
  );
  section(t('projects.analysis.sections.placement'), [
    line(t('projects.analysis.placement.domain'), c.placement.domain),
    c.placement.text,
    c.placement.attaches.length
      ? line(t('projects.analysis.placement.attaches'), c.placement.attaches.map((a) => a.label).join(', '))
      : '',
  ]);
  section(
    t('projects.analysis.sections.reusable'),
    c.reusable.map((r) => `- **${r.label}**${r.what ? ` — ${r.what}` : ''}${r.how ? ` (${r.how})` : ''}`)
  );
  section(
    t('projects.analysis.sections.carriers'),
    c.carriers.map((r) => `- **${r.label}**${r.role ? `, ${r.role}` : ''}${r.why ? ` — ${r.why}` : ''}`)
  );
  section(t('projects.analysis.sections.technologies'), [
    ...c.technologies.existing.map((x) => `- ${x.label}${x.usage ? ` — ${x.usage}` : ''}`),
    ...c.technologies.toIntroduce.map(
      (x) => `- ${t('projects.analysis.technologies.toIntroduceShort')} : ${x.label}${x.why ? ` — ${x.why}` : ''}`
    ),
  ]);
  section(
    t('projects.analysis.sections.vigilance'),
    c.vigilance.map(
      (v) => `- **${v.title}** (${t(`projects.analysis.severity.${v.severity}`)})${v.detail ? ` — ${v.detail}` : ''}`
    )
  );
  section(t('projects.analysis.sections.synthesis'), [
    c.synthesis.summary,
    '',
    ...c.synthesis.actions.map(
      (a, i) => `${i + 1}. ${a.step}${[a.who, a.resource].filter(Boolean).length ? ` (${[a.who, a.resource].filter(Boolean).join(' · ')})` : ''}`
    ),
  ]);
  section(
    t('projects.analysis.sections.sources'),
    c.citations.map((x) => `- [${x.n}] ${x.documentName ?? ''}${x.excerpt ? ` — « ${x.excerpt} »` : ''}`)
  );
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}
